/**
 * Error thrown by the ErrorInterceptor in place of the plain `Error` it used
 * to build. Keeps the HTTP status, the backend's machine-readable `error_code`
 * and the raw body, so components can branch on them (retry-after-429, DSP
 * credential failures...) while every existing `error.message` consumer keeps
 * working unchanged.
 */
export class ApiError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly errorCode: string | null = null,
        public readonly body: any = null
    ) {
        super(message);
        this.name = "ApiError";
    }
}

/** Backend error codes for DSP credential failures (HTTP 409). */
export const DSP_CREDENTIALS_EXPIRED = "DSP_CREDENTIALS_EXPIRED";
export const DSP_CREDENTIALS_INVALID = "DSP_CREDENTIALS_INVALID";

/** The organization has no DSP credentials stored: never registered, or removed by a super-admin (HTTP 409). */
export const DSP_CREDENTIALS_MISSING = "DSP_CREDENTIALS_MISSING";

/**
 * Registering credentials: the DSP accepts the login, but the organization it belongs to no longer
 * exists there (HTTP 409). Only the super-admin credentials screen gets it.
 */
export const DSP_ORGANIZATION_NOT_FOUND = "DSP_ORGANIZATION_NOT_FOUND";

/**
 * The DSP a request needs isn't available right now, e.g. its credentials aren't configured
 * (HTTP 503). An expected condition, not an outage: the user stays on the page and the component
 * shows the neutral API message.
 */
export const DSP_UNAVAILABLE = "DSP_UNAVAILABLE";

/** The action only exists for standard campaigns, not for DOOH Direct ones (HTTP 422). */
export const DSP_ACTION_NOT_SUPPORTED = "DSP_ACTION_NOT_SUPPORTED";

/**
 * The DSP didn't answer in time and the operation may have gone through anyway (HTTP 504). The API
 * message tells the user not to retry: a blind retry is how duplicates are born, so it must reach
 * the screen instead of the generic "please try again".
 */
export const DSP_TIMEOUT = "DSP_TIMEOUT";

/**
 * The DSP didn't confirm the cancellation, so nothing was cancelled nor deleted (HTTP 502). The API
 * message says so; the generic "please try again" would hide that the item is still live.
 */
export const DSP_CANCEL_FAILED = "DSP_CANCEL_FAILED";

/**
 * The DSP refused the operation although the credentials are fine: the item belongs to another
 * organization than the one the request ran as (HTTP 409). The API message says which way to go
 * (pick that organization in the selector); "renew your credentials" would be the wrong advice.
 */
export const DSP_OPERATION_NOT_ALLOWED = "DSP_OPERATION_NOT_ALLOWED";

/**
 * Custom report (`/analytics/custom-report`) whose campaigns are partly DOOH Direct and partly not
 * (HTTP 422). Each DSP's data lives in a different place, so the API answers one or the other.
 */
export const REPORT_MIXED_DSP = "REPORT_MIXED_DSP";

/**
 * Custom report of DOOH Direct campaigns asking for a drilldown, KPI or filter they don't have
 * (HTTP 422). The body lists them under `unsupported: {drilldown, kpis, rules}`.
 */
export const REPORT_UNSUPPORTED_FOR_DSP = "REPORT_UNSUPPORTED_FOR_DSP";

export function isDspCredentialsError(error: unknown): error is ApiError {
    return (
        error instanceof ApiError &&
        (error.errorCode === DSP_CREDENTIALS_EXPIRED ||
            error.errorCode === DSP_CREDENTIALS_INVALID ||
            error.errorCode === DSP_CREDENTIALS_MISSING)
    );
}
